import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Api from "../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../core/data/redux/apis/urls";
import { toast } from "react-toastify";
import DOMPurify from "dompurify";

interface Message {
    id: number;
    text: string;
    isBot: boolean;
    timestamp: Date;
    isTyping?: boolean;
}

interface QuickReply {
    text: string;
    icon: string;
    key?: string;
}

const ChatInterface: React.FC = () => {
    const navigate = useNavigate();
    const [messages, setMessages] = useState<Message[]>([]);
    const [inputText, setInputText] = useState("");
    // Preload speech synthesis voices (fixes Edge/Chrome empty voices bug)
    useEffect(() => {
        if ('speechSynthesis' in window) {
            window.speechSynthesis.getVoices();
            // Optional: listen for voices changed
            if (window.speechSynthesis.onvoiceschanged !== undefined) {
                window.speechSynthesis.onvoiceschanged = () => window.speechSynthesis.getVoices();
            }
        }
    }, []);

    const [isTyping, setIsTyping] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [navigationKey, setNavigationKey] = useState<string | null>(null);
    const [sessionId, setSessionId] = useState<string | null>(null);
    const sessionIdRef = useRef<string | null>(null);
    const [isListening, setIsListening] = useState(false);
    const isListeningRef = useRef(false);
    const [speakingMessageId, setSpeakingMessageId] = useState<number | null>(null);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    const audioRef = useRef<HTMLAudioElement | null>(null);

    const speak = async (text: string, id: number) => {
        if (isListeningRef.current) return;

        if (speakingMessageId === id) {
            window.speechSynthesis.cancel();
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
            setSpeakingMessageId(null);
            return;
        }

        window.speechSynthesis.cancel();
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
        }

        setSpeakingMessageId(id);

        console.log("🔊 Playing Browser Built-in Voice");
        if ('speechSynthesis' in window) {
            const utterance = new SpeechSynthesisUtterance(text);

            const voices = window.speechSynthesis.getVoices();
            const indianFemaleVoice = voices.find(voice =>
                (voice.lang.includes('IN') || voice.lang.includes('hi')) &&
                (voice.name.toLowerCase().includes('female') ||
                    voice.name.toLowerCase().includes('heera') ||
                    voice.name.toLowerCase().includes('veena') ||
                    voice.name.toLowerCase().includes('aditi') ||
                    voice.name.toLowerCase().includes('pallavi') ||
                    voice.name.toLowerCase().includes('neerja') ||
                    voice.name.toLowerCase().includes('swara'))
            ) || voices.find(voice => voice.lang.includes('en-IN') || voice.lang.includes('hi-IN')) || voices[0];

            if (indianFemaleVoice) {
                utterance.voice = indianFemaleVoice;
                utterance.lang = indianFemaleVoice.lang;
            } else {
                utterance.lang = 'en-IN';
            }

            utterance.rate = 1.0;
            utterance.pitch = 1.0;
            utterance.volume = 1.0;
            utterance.onend = () => setSpeakingMessageId(null);
            utterance.onerror = () => setSpeakingMessageId(null);

            window.speechSynthesis.speak(utterance);
        } else {
            toast.error("Text to speech not supported in this browser.");
            setSpeakingMessageId(null);
        }
    };

    const playBeep = (type: 'on' | 'off') => {
        try {
            const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
            if (!AudioContext) return;
            const ctx = new AudioContext();

            const playTone = (freq: number, startTime: number, duration: number) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.connect(gain);
                gain.connect(ctx.destination);

                osc.type = 'sine';
                osc.frequency.setValueAtTime(freq, startTime);

                gain.gain.setValueAtTime(0, startTime);
                gain.gain.linearRampToValueAtTime(0.5, startTime + 0.02);
                gain.gain.exponentialRampToValueAtTime(0.01, startTime + duration);

                osc.start(startTime);
                osc.stop(startTime + duration);
            };

            if (type === 'on') {
                // Ascending double-chime for "Mic On"
                playTone(600, ctx.currentTime, 0.15);
                playTone(800, ctx.currentTime + 0.15, 0.2);
            } else {
                // Descending double-chime for "Mic Off"
                playTone(600, ctx.currentTime, 0.15);
                playTone(450, ctx.currentTime + 0.15, 0.2);
            }
        } catch (e) {
            console.error("Audio playback failed", e);
        }
    };

    const toggleListening = async () => {
        if (isListeningRef.current) {
            playBeep('off');
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
                mediaRecorderRef.current.stop();
            }
            setIsListening(false);
            isListeningRef.current = false;
            return;
        }

        // Stop any currently playing speech when mic is turned on
        window.speechSynthesis.cancel();
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
        }
        setSpeakingMessageId(null);

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorderRef.current = mediaRecorder;
            audioChunksRef.current = [];

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorder.onstart = () => {
                playBeep('on');
                setIsListening(true);
                isListeningRef.current = true;
            };

            mediaRecorder.onstop = async () => {
                stream.getTracks().forEach(track => track.stop());

                if (audioChunksRef.current.length > 0) {
                    const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });

                    setIsTyping(true);

                    try {
                        const formData = new FormData();
                        formData.append('model', 'saaras:v3');
                        formData.append('mode', 'transcribe');
                        formData.append('file', audioBlob, 'speech.wav');

                        // Use server proxy or environment variable if configured (Finding 6 remediation)
                        const sarvamKey = process.env.REACT_APP_SARVAM_API_KEY || "";
                        const headers: Record<string, string> = {};
                        if (sarvamKey) {
                            headers['api-subscription-key'] = sarvamKey;
                        }

                        const response = await fetch('https://api.sarvam.ai/speech-to-text', {
                            method: 'POST',
                            headers,
                            body: formData
                        });

                        if (!response.ok) {
                            throw new Error("Speech service currently unavailable");
                        }

                        const data = await response.json();
                        if (data && data.transcript && data.transcript.trim()) {
                            handleSend(data.transcript, true);
                        } else if (!data.transcript) {
                            toast.error("Could not transcribe audio.");
                        }
                    } catch (error) {
                        console.error("Transcription error:", error);
                        toast.error("Speech transcription service is currently unavailable.");
                    } finally {
                        setIsTyping(false);
                    }
                }
            };

            mediaRecorder.start();
        } catch (error) {
            console.error("Microphone access denied or not supported:", error);
            toast.error("Microphone access required for speech recognition.");
        }
    };

    // DMRC responses based on keywords
    const dmrcResponses: Record<string, string> = {
        "leave policy": `📋 **Leave Policy Information**

✓ Earned Leave: 30 days annually
✓ Half-Pay Leave: 20 days annually
✓ Casual Leave: 15 days annually

For your current leave balance, please check the employee portal or contact HR at ext. 2345.`,
        "attendance": `⏰ **Attendance Information**

Your attendance records are available in the employee portal. You can check:
- Daily attendance logs
- Monthly attendance summary
- Leave balance and history

For attendance-related queries, contact HR at ext. 2345.`,
        "payroll": `💰 **Payroll Information**

Payroll details including salary slips, deductions, and tax information are available in the employee portal.

For payroll queries, contact Accounts Department at ext. 2000.`,
        "holidays": `🗓️ **Holiday Information**

DMRC follows the calendar year for holidays. You can view the complete holiday list in the employee portal under "DMRC Calendar" section.

For holiday-related queries, contact HR at ext. 2345.`,
        default: `I'm here to help you with DMRC-related queries. You can ask me about:
- Leave policies
- Attendance
- Payroll
- Holidays
- Or any other employee-related information

How can I assist you today?`,
    };

    // Initialize with welcome message
    useEffect(() => {
        const welcomeMessage: Message = {
            id: 1,
            text: "Welcome to the DMRC Employee Assistant! 👋\n\nI'm here to help you with all your queries. How can I assist you today?",
            isBot: true,
            timestamp: new Date(),
        };
        setMessages([welcomeMessage]);
    }, []);

    // Auto scroll to bottom when new messages arrive
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, isTyping]);

    // Simulate typing indicator
    const simulateTyping = () => {
        setIsTyping(true);
        const typingMessage: Message = {
            id: Date.now(),
            text: "",
            isBot: true,
            timestamp: new Date(),
            isTyping: true,
        };
        setMessages((prev) => [...prev, typingMessage]);
    };

    // Send message to API
    const sendChat = useCallback(async (text: string, currentSessionId: string | null) => {
        const payload: any = { message: text, language: "en" };
        if (currentSessionId) {
            payload.session_id = currentSessionId;
        }
        console.log("AI CHAT SEND MESSAGE values:", payload);
        try {
            const res = await Api.post(API_URLS.AICHAT.SEND_MESSAGE, payload, true);
            console.log("AI CHAT Response:", res);
            return res;
        } catch (err: any) {
            console.log("AI CHAT SEND MESSAGE Error:", err);
            toast.error(err?.message || "Failed to send message");
            throw err;
        }
    }, []);

    // Handle send message
    const handleSend = async (text?: string, isVoice: boolean = false) => {
        const messageText = text || inputText.trim();
        if (!messageText) return;

        // Add user message
        const userMessage: Message = {
            id: Date.now(),
            text: messageText,
            isBot: false,
            timestamp: new Date(),
        };
        setMessages((prev) => [...prev, userMessage]);
        setInputText("");
        inputRef.current?.blur();

        // Show typing indicator
        simulateTyping();

        try {
            // Send to API
            const response = await sendChat(messageText, sessionIdRef.current);

            // Remove typing indicator
            setIsTyping(false);
            setMessages((prev) => prev.filter((msg) => !msg.isTyping));

            const responseData = response?.data || response || {};
            let responseText = dmrcResponses.default;

            // Check if response contains navigation key
            if (responseData?.navigation_key) {
                // Handle navigation from backend
                const navKey = responseData.navigation_key;
                // Store navigation key for later use
                setNavigationKey(navKey);
                console.log("Navigation key received:", navKey);
            }

            if (responseData?.session_id) {
                setSessionId(responseData.session_id);
                sessionIdRef.current = responseData.session_id;
            }
            // Use API response if available, otherwise use keyword matching
            if (responseData?.answer || responseData?.message || responseData?.response) {
                responseText = responseData.answer || responseData.message || responseData.response;
            } else {
                // Fallback to keyword matching
                const query = messageText.toLowerCase();
                for (const [key, response] of Object.entries(dmrcResponses)) {
                    if (key !== "default" && query.includes(key)) {
                        responseText = response;
                        break;
                    }
                }
            }

            // Add bot response
            const botMessage: Message = {
                id: Date.now() + 1,
                text: responseText,
                isBot: true,
                timestamp: new Date(),
            };
            setMessages((prev) => [...prev, botMessage]);

            if (isVoice) {
                // Remove Markdown and HTML before speaking
                const plainText = responseText.replace(/\*\*/g, '').replace(/<[^>]+>/g, '').replace(/✓/g, '').replace(/#/g, '');
                speak(plainText, botMessage.id);
            }
        } catch (error) {
            // Remove typing indicator on error
            setIsTyping(false);
            setMessages((prev) => prev.filter((msg) => !msg.isTyping));

            // Show error message
            const errorMessage: Message = {
                id: Date.now() + 1,
                text: "Sorry, I encountered an error. Please try again later.",
                isBot: true,
                timestamp: new Date(),
            };
            setMessages((prev) => [...prev, errorMessage]);
        }
    };

    // Handle quick reply click
    const handleQuickReply = (quickReply: QuickReply) => {
        handleSend(quickReply.text);
    };

    // Format timestamp
    const formatTime = (date: Date) => {
        return date.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        });
    };

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                height: "100vh",
                backgroundColor: "#f5f5f5",
                fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
        >
            {/* Header */}
            <div
                style={{
                    background: "linear-gradient(135deg, #c0282c 0%, #e03236 100%)",
                    color: "white",
                    padding: "16px",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}
            >
                <button
                    onClick={() => {
                        setSessionId(null);
                        navigate(-1);
                    }}
                    style={{
                        background: "none",
                        border: "none",
                        color: "white",
                        fontSize: "20px",
                        cursor: "pointer",
                        padding: "4px",
                        display: "flex",
                        alignItems: "center",
                    }}
                >
                    ←
                </button>
                <div
                    style={{
                        width: "36px",
                        height: "36px",
                        borderRadius: "50%",
                        backgroundColor: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden"
                    }}
                >
                    <img src="/assets/img/login/ladies.png" alt="DMRC Assistant" style={{ width: "100%", height: "100%", objectFit: "contain", transform: "scale(1.15)" }} />
                </div>
                <div>
                    <h4 style={{ margin: 0, fontSize: "18px", fontWeight: "600", color: "white" }}>
                        DMRC Assistant
                    </h4>
                    <small style={{ opacity: 0.9, fontSize: "12px" }}>Online</small>
                </div>
            </div>

            {/* Messages Area */}
            <div
                className="chatbot-messages-scroll"
                style={{
                    flex: 1,
                    overflowY: "auto",
                    padding: "16px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                }}
            >
                {/* Spacer to push messages to bottom */}
                <div style={{ flex: 1, minHeight: "min-content" }} />
                {messages.map((message) => (
                    <div
                        key={message.id}
                        style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "8px",
                            justifyContent: message.isBot ? "flex-start" : "flex-end",
                        }}
                    >
                        {message.isBot && (
                            <div
                                style={{
                                    width: "32px",
                                    height: "32px",
                                    borderRadius: "50%",
                                    backgroundColor: "white",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                    fontSize: "18px",
                                    overflow: "hidden"
                                }}
                            >
                                <img src="/assets/img/login/ladies.png" alt="bot" style={{ width: "100%", height: "100%", objectFit: "contain", transform: "scale(1.15)" }} />
                            </div>
                        )}
                        <div
                            style={{
                                maxWidth: "75%",
                                padding: "12px 16px",
                                borderRadius: message.isBot ? "16px 16px 16px 4px" : "16px 16px 4px 16px",
                                background: message.isBot ? "white" : "linear-gradient(135deg, #c0282c 0%, #e03236 100%)",
                                color: message.isBot ? "#333" : "white",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                                wordWrap: "break-word",
                                animation: "messagePop 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards",
                            }}
                        >
                            {message.isTyping ? (
                                <div style={{ display: "flex", gap: "4px" }}>
                                    <div
                                        style={{
                                            width: "8px",
                                            height: "8px",
                                            borderRadius: "50%",
                                            backgroundColor: "#999",
                                            animation: "bounce 1.4s infinite",
                                        }}
                                    />
                                    <div
                                        style={{
                                            width: "8px",
                                            height: "8px",
                                            borderRadius: "50%",
                                            backgroundColor: "#999",
                                            animation: "bounce 1.4s infinite 0.2s",
                                        }}
                                    />
                                    <div
                                        style={{
                                            width: "8px",
                                            height: "8px",
                                            borderRadius: "50%",
                                            backgroundColor: "#999",
                                            animation: "bounce 1.4s infinite 0.4s",
                                        }}
                                    />
                                </div>
                            ) : (
                                <>
                                    <div
                                        style={{
                                            whiteSpace: "pre-wrap",
                                            fontSize: "14px",
                                            lineHeight: "1.5",
                                        }}
                                        dangerouslySetInnerHTML={{
                                            __html: DOMPurify.sanitize(
                                                message.text
                                                    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
                                                    .replace(/✓/g, "✓")
                                                    .replace(/\n/g, "<br />")
                                            ),
                                        }}
                                    />
                                    <div
                                        style={{
                                            fontSize: "11px",
                                            opacity: 0.7,
                                            marginTop: "4px",
                                            textAlign: message.isBot ? "right" : "left",
                                        }}
                                    >
                                        {formatTime(message.timestamp)}
                                    </div>
                                </>
                            )}
                        </div>
                        {message.isBot && !message.isTyping && (
                            <button
                                onClick={() => speak(message.text, message.id)}
                                style={{
                                    width: "30px",
                                    height: "30px",
                                    borderRadius: "50%",
                                    backgroundColor: speakingMessageId === message.id ? "white" : "#c0282c",
                                    border: "none",
                                    color: speakingMessageId === message.id ? "#c0282c" : "white",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    marginLeft: "8px",
                                    alignSelf: "flex-end",
                                    marginBottom: "4px",
                                    flexShrink: 0,
                                    boxShadow: speakingMessageId === message.id ? "0 2px 8px rgba(192,40,44,0.4)" : "none",
                                    transition: "all 0.3s",
                                    animation: speakingMessageId === message.id ? "pulseVoice 1.5s infinite" : "none"
                                }}
                                title={speakingMessageId === message.id ? "Stop playing" : "Play message"}
                            >
                                {speakingMessageId === message.id ? (
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                        <rect x="6" y="6" width="12" height="12" rx="2" ry="2"></rect>
                                    </svg>
                                ) : (
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                                        <path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path>
                                        <path d="M19.07 4.93a10 10 0 0 1 0 14.14"></path>
                                    </svg>
                                )}
                            </button>
                        )}
                    </div>
                ))}

                {/* Quick Actions */}
                {messages.length === 1 && (
                    <div style={{ marginTop: "8px" }}>
                        <div
                            style={{
                                fontSize: "12px",
                                color: "#666",
                                marginBottom: "12px",
                            }}
                        >
                            Quick Actions:
                        </div>
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(2, 1fr)",
                                gap: "8px",
                            }}
                        >

                        </div>
                    </div>
                )}

                <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div
                style={{
                    padding: "12px 16px",
                    backgroundColor: "#f5f5f5",
                    borderTop: "1px solid #e0e0e0",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                }}
            >
                <div style={{
                    display: "flex",
                    flex: 1,
                    backgroundColor: "white",
                    borderRadius: "24px",
                    padding: "4px 8px",
                    alignItems: "center",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
                }}>
                    <input
                        ref={inputRef}
                        type="text"
                        value={inputText}
                        onChange={(e) => setInputText(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === "Enter" && !e.shiftKey && !isTyping) {
                                e.preventDefault();
                                handleSend();
                            }
                        }}
                        placeholder="Please type here..."
                        style={{
                            flex: 1,
                            padding: "10px",
                            border: "none",
                            borderRadius: "24px",
                            fontSize: "14px",
                            outline: "none",
                            backgroundColor: "transparent"
                        }}
                    />
                    <button
                        onClick={toggleListening}
                        disabled={isTyping}
                        style={{
                            width: "36px",
                            height: "36px",
                            borderRadius: "50%",
                            backgroundColor: isListening ? "white" : (isTyping ? "#e0e0e0" : "#c0282c"),
                            border: isListening ? "1px solid #c0282c" : "none",
                            color: isListening ? "#c0282c" : (isTyping ? "#999" : "white"),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: isTyping ? "not-allowed" : "pointer",
                            transition: "all 0.3s",
                            marginRight: "4px",
                            flexShrink: 0,
                            animation: isListening ? "pulseVoice 1.5s infinite" : "none",
                            opacity: isTyping ? 0.6 : 1
                        }}
                        title={isListening ? "Stop listening" : "Start speaking"}
                    >
                        {isListening ? (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="9" y="9" width="6" height="6" rx="1" ry="1"></rect>
                                <circle cx="12" cy="12" r="10"></circle>
                            </svg>
                        ) : (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
                                <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
                                <line x1="12" y1="19" x2="12" y2="23"></line>
                                <line x1="8" y1="23" x2="16" y2="23"></line>
                            </svg>
                        )}
                    </button>
                </div>
                <button
                    onClick={() => handleSend()}
                    disabled={!inputText.trim() || isTyping}
                    style={{
                        width: "44px",
                        height: "44px",
                        borderRadius: "50%",
                        backgroundColor: "white",
                        border: "1px solid #e0e0e0",
                        color: inputText.trim() && !isTyping ? "#c0282c" : "#999",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: inputText.trim() && !isTyping ? "pointer" : "not-allowed",
                        transition: "all 0.2s",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                        flexShrink: 0
                    }}
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="22" y1="2" x2="11" y2="13"></line>
                        <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                    </svg>
                </button>
            </div>

            {/* Fixed Navigation Button */}
            <button
                onClick={() => {
                    // Handle navigation based on backend key or default to dashboard
                    if (navigationKey) {
                        navigate(navigationKey);
                    } else {
                        navigate("/dashboard");
                    }
                }}
                style={{
                    position: "fixed",
                    bottom: "80px",
                    right: "20px",
                    width: "56px",
                    height: "56px",
                    borderRadius: "50%",
                    backgroundColor: "#c0282c",
                    border: "none",
                    color: "white",
                    fontSize: "24px",
                    cursor: "pointer",
                    boxShadow: "0 4px 12px rgba(192, 40, 44, 0.4)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 1000,
                    transition: "all 0.3s",
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "scale(1.1)";
                    e.currentTarget.style.boxShadow = "0 6px 16px rgba(192, 40, 44, 0.5)";
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(192, 40, 44, 0.4)";
                }}
                title="Navigate"
            >
                🏠
            </button>

            {/* CSS for typing animation */}
            <style>{`
        @keyframes pulseVoice {
          0% {
            box-shadow: 0 0 0 0 rgba(192, 40, 44, 0.4);
            transform: scale(0.95);
          }
          70% {
            box-shadow: 0 0 0 6px rgba(192, 40, 44, 0);
            transform: scale(1.05);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(192, 40, 44, 0);
            transform: scale(0.95);
          }
        }
        
        @keyframes bounce {
          0%, 60%, 100% {
            transform: translateY(0);
          }
          30% {
            transform: translateY(-10px);
          }
        }
        
        @keyframes messagePop {
          from {
            opacity: 0;
            transform: translateY(10px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        .chatbot-messages-scroll::-webkit-scrollbar {
          width: 6px;
        }
        .chatbot-messages-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .chatbot-messages-scroll::-webkit-scrollbar-thumb {
          background-color: rgba(0,0,0,0.15);
          border-radius: 10px;
        }
        .chatbot-messages-scroll::-webkit-scrollbar-thumb:hover {
          background-color: rgba(0,0,0,0.3);
        }
      `}</style>
        </div>
    );
};

export default ChatInterface;

